if (!customElements.get("blaze-hero-carousel")) {
  customElements.define(
    "blaze-hero-carousel",
    class BlazeHeroCarousel extends HTMLElement {
      constructor() {
        super();
        this.currentSlideIndex = 0;
        this.initializationAttempts = 0;
        this.maxAttempts = 10;
        this.autoplayTimer = null;
        this.autoplayDelay = 3000;
        this.enableAutoplay = true;
      }

      connectedCallback() {
        this.initializeBlazeSlider();
      }

      initializeBlazeSlider() {
        const sliderElement = this.querySelector(".blaze-slider");
        if (!sliderElement) {
          console.error("Blaze slider element not found");
          return;
        }

        // Remove hidden class from all slides to let Blaze Slider control visibility
        const slides = sliderElement.querySelectorAll(".blaze-slide");
        slides.forEach((slide) => {
          slide.classList.remove("hidden");
        });

        // Check if BlazeSlider is available
        if (typeof window.BlazeSlider === "undefined") {
          this.initializationAttempts++;

          if (this.initializationAttempts < this.maxAttempts) {
            setTimeout(() => this.initializeBlazeSlider(), 200);
          } else {
            console.error(
              "BlazeSlider failed to load after",
              this.maxAttempts,
              "attempts"
            );
          }
          return;
        }

        // Get autoplay settings from the section
        this.autoplayDelay = parseInt(this.getAttribute("data-delay") || 3000);
        this.enableAutoplay = this.getAttribute("data-autoplay") !== "false";

        try {
          // Initialize Blaze Slider without autoplay - we'll handle it manually
          const blazeSlider = new window.BlazeSlider(sliderElement, {
            all: {
              enableAutoplay: false, // Disable built-in autoplay
              enablePagination: false, // Disable built-in pagination - we create custom pagination
              slidesToShow: 1,
              slidesToScroll: 1,
              transitionDuration: 400,
              transitionTimingFunction: "linear",
              loop: true,
              enableAutoplayPauseOnHover: false,
              draggable: true,
            },
          });


          // Store the blaze slider instance
          this.blazeSlider = blazeSlider;

          // Create pagination after slider is initialized
          this.createPagination(sliderElement);

          // Handle slide change events for deferred media and GIFs
          blazeSlider.onSlide(
            (pageIndex, firstVisibleSlideIndex, lastVisibleSlideIndex) => {
              this.handleSlideChange(pageIndex, firstVisibleSlideIndex);
              this.updatePagination(pageIndex);
              this.startProgressAnimation(pageIndex, this.autoplayDelay);
              this.resetAutoplayTimer();
            }
          );

          // Show the slider after initialization
          sliderElement.style.opacity = 1;
          sliderElement.classList.remove("slider-loading");

          // Play the first slide's media if it exists
          const firstSlide = sliderElement.querySelector(".blaze-slide");
          if (firstSlide) {
            this.handleSlideChange(0, 0);
            this.updatePagination(0);
            this.startProgressAnimation(0, this.autoplayDelay);
            this.startAutoplayTimer();
          }
        } catch (error) {
          console.error("Error initializing BlazeSlider:", error);
        }
      }

      handleSlideChange(pageIndex, firstVisibleSlideIndex) {
        // Get all slides - BlazeSlider reorders the DOM so the active slide is always first
        const slides = this.querySelectorAll(".blaze-slide");

        // The active slide is always the first slide in the DOM due to BlazeSlider's reordering
        const activeSlide = slides[0];
        const actualSlideIndex = firstVisibleSlideIndex; // This tells us which actual slide content is showing

        slides.forEach((slide, domIndex) => {
          // Handle deferred media toggle
          slide.querySelectorAll("deferred-media").forEach((element) => {
            if (domIndex === 0) {
              // Only the first slide in DOM is active
              element.togglePlayer(false, false); // Play media

              // Update the button to show pause state
              this.updatePlayButtonState(element, true);

              // Add video end event listener for this slide
              this.setupVideoEndListener(element, actualSlideIndex);
              
              // Set up play/pause button handler to manage video state
              this.setupPlayPauseHandler(element);
            } else {
              element.togglePlayer(true); // Pause media

              // Update the button to show play state
              this.updatePlayButtonState(element, false);
            }
          });

          // Handle GIF playback
          const gifContainer = slide.querySelector("[data-gif-container] img");
          if (gifContainer && domIndex === 0) {
            // Only the first slide in DOM is active
            const baseSrc = gifContainer.dataset.baseSrc;
            const baseSrcset = gifContainer.dataset.baseSrcset;
            if (baseSrc) {
              gifContainer.setAttribute(
                "src",
                baseSrc.replaceAll("&width", `&reload=${Math.random()}&width`)
              );
            }
            if (baseSrcset) {
              gifContainer.setAttribute(
                "srcset",
                baseSrcset.replaceAll(
                  "&width",
                  `&reload=${Math.random()}&width`
                )
              );
            }
          }
        });
      }

      setupVideoEndListener(deferredMediaElement, slideIndex) {
        // Remove any existing listeners
        if (deferredMediaElement._videoEndListener) {
          deferredMediaElement.removeEventListener(
            "ended",
            deferredMediaElement._videoEndListener
          );
        }

        // Create new listener
        deferredMediaElement._videoEndListener = () => {
          if (this.blazeSlider) {
            this.blazeSlider.next();
          }
        };

        // Add the listener
        deferredMediaElement.addEventListener(
          "ended",
          deferredMediaElement._videoEndListener
        );

        // Also listen for iframe load to set up YouTube/Vimeo end detection
        const iframe = deferredMediaElement.querySelector("iframe");
        if (iframe) {
          iframe.addEventListener("load", () => {
            this.setupIframeVideoEndDetection(iframe, slideIndex);
          });
        }
      }

      setupIframeVideoEndDetection(iframe, slideIndex) {
        try {
          // For YouTube videos
          if (iframe.src.includes("youtube.com")) {
            // YouTube doesn't fire 'ended' events reliably, so we'll use a timeout
            // based on typical video duration or let the autoplay interval handle it
          }
          // For Vimeo videos
          else if (iframe.src.includes("vimeo.com")) {
            // Vimeo also doesn't fire 'ended' events reliably in background mode
          }
        } catch (error) {
          // Could not set up iframe video end detection
        }
      }

      setupPlayPauseHandler(deferredMediaElement) {
        const playButton = deferredMediaElement.querySelector(
          ".deferred-media__poster-button"
        );
        if (!playButton) return;

        // Remove existing listener if any
        if (playButton._clickHandler) {
          playButton.removeEventListener("click", playButton._clickHandler);
        }

        // Create new click handler
        playButton._clickHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();

          // Get current video/iframe element
          const video = deferredMediaElement.querySelector("video");
          const iframe = deferredMediaElement.querySelector("iframe");
          
          if (video) {
            // Handle HTML5 video
            if (video.paused) {
              video.play();
              this.updatePlayButtonState(deferredMediaElement, true);
            } else {
              video.pause();
              this.updatePlayButtonState(deferredMediaElement, false);
            }
          } else if (iframe) {
            // For iframe videos (YouTube/Vimeo), we'll rely on the deferred-media toggle
            const isCurrentlyPlaying = playButton.getAttribute("aria-label") === "Pause video";
            deferredMediaElement.togglePlayer(!isCurrentlyPlaying, false);
            this.updatePlayButtonState(deferredMediaElement, !isCurrentlyPlaying);
          }
        };

        playButton.addEventListener("click", playButton._clickHandler);
      }

      updatePlayButtonState(deferredMediaElement, isPlaying) {
        // Find the play button within the deferred-media element
        const playButton = deferredMediaElement.querySelector(
          ".deferred-media__poster-button"
        );
        if (!playButton) return;

        // Update the button text and aria-label
        const buttonText = playButton.querySelector(
          ".deferred-media__poster-button-text"
        );
        const buttonIcon = playButton.querySelector(
          ".deferred-media__poster-button-icon"
        );

        if (isPlaying) {
          // Show pause button
          if (buttonText) buttonText.textContent = "Pause";
          if (buttonIcon) {
            buttonIcon.innerHTML =
              '<svg aria-hidden="true" focusable="false" class="icon icon-pause" fill="currentColor" aria-hidden="true" focusable="false" viewBox="0 0 10 13"><path fill-rule="evenodd" clip-rule="evenodd" d="M0 0h3v13H0V0zm7 0h3v13H7V0z"/></svg>';
          }
          playButton.setAttribute("aria-label", "Pause video");
        } else {
          // Show play button
          if (buttonText) buttonText.textContent = "Play";
          if (buttonIcon) {
            buttonIcon.innerHTML =
              '<svg aria-hidden="true" focusable="false" class="icon icon-play" fill="currentColor" aria-hidden="true" focusable="false" viewBox="0 0 10 13"><path fill-rule="evenodd" clip-rule="evenodd" d="M8.447 3.276A1 1 0 0 0 7 4v5a1 1 0 0 0 1.447.894l5-2.5A1 1 0 0 0 14 7.5v-1a1 1 0 0 0-1.447-.894l-4.106 2.053L8.447 3.276Z"/></svg>';
          }
          playButton.setAttribute("aria-label", "Play video");
        }
      }

      createPagination(sliderElement) {
        const slides = sliderElement.querySelectorAll(".blaze-slide");
        if (slides.length <= 1) return;

        // Check if pagination already exists
        let pagination = sliderElement.querySelector(".blaze-pagination");
        if (pagination) {
          pagination.remove();
        }

        pagination = document.createElement("div");
        pagination.className = "blaze-pagination";

        slides.forEach((_, index) => {
          const button = document.createElement("button");
          button.setAttribute("aria-label", `Go to slide ${index + 1}`);

          // Add progress bar elements
          const progressBackground = document.createElement("span");
          progressBackground.className = "blaze-progress-background";

          const progressBar = document.createElement("span");
          progressBar.className = "blaze-progress-bar";

          button.appendChild(progressBackground);
          button.appendChild(progressBar);

          button.addEventListener("click", () => {
            if (this.blazeSlider) {
              this.goToSlide(index);
            }
          });
          pagination.appendChild(button);
        });

        sliderElement.appendChild(pagination);
        this.pagination = pagination;
      }

      goToSlide(targetIndex) {
        if (!this.blazeSlider || this.currentSlideIndex === targetIndex) return;

        const difference = targetIndex - this.currentSlideIndex;
        if (difference > 0) {
          this.blazeSlider.next(difference);
        } else {
          this.blazeSlider.prev(Math.abs(difference));
        }
        
        // Reset the autoplay timer when manually navigating
        this.resetAutoplayTimer();
      }

      updatePagination(currentSlideIndex) {
        if (!this.pagination) return;

        this.currentSlideIndex = currentSlideIndex;
        const buttons = this.pagination.querySelectorAll("button");
        buttons.forEach((button, index) => {
          if (index === currentSlideIndex) {
            button.classList.add("active");
          } else {
            button.classList.remove("active");
          }
        });
      }

      startProgressAnimation(slideIndex, duration) {
        if (!this.pagination) return;

        const buttons = this.pagination.querySelectorAll("button");
        const activeButton = buttons[slideIndex];

        if (!activeButton) return;

        // Reset all progress bars
        buttons.forEach((button) => {
          const progressBar = button.querySelector(".blaze-progress-bar");
          if (progressBar) {
            progressBar.style.width = "0%";
            progressBar.style.animation = "none";
          }
        });

        // Start progress animation for active slide
        const progressBar = activeButton.querySelector(".blaze-progress-bar");
        if (progressBar) {
          // Reset and start animation
          progressBar.style.animation = "none";
          progressBar.offsetHeight; // Trigger reflow
          progressBar.style.animation = `blazeCountingBar ${duration}ms linear forwards`;
        }
      }

      startAutoplayTimer() {
        if (!this.enableAutoplay) return;
        
        this.clearAutoplayTimer();
        
        this.autoplayTimer = setTimeout(() => {
          if (this.blazeSlider) {
            this.blazeSlider.next();
          }
        }, this.autoplayDelay);
      }

      resetAutoplayTimer() {
        if (!this.enableAutoplay) return;
        this.startAutoplayTimer();
      }

      clearAutoplayTimer() {
        if (this.autoplayTimer) {
          clearTimeout(this.autoplayTimer);
          this.autoplayTimer = null;
        }
      }

      get sliderElement() {
        return this.querySelector(".blaze-slider");
      }
    }
  );
}
